import React, { Component } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { LoginState, LoginProps } from '../../interfaces/login';
import { loginUser } from '../../redux/actions/user';

// Define interface for state ()
interface State {
  email: string;
  password: string;
  touched: {
    email: boolean;
    password: boolean;
  };
}

class Login extends Component<LoginProps, State> {
  constructor(props: LoginProps) {
    super(props);
    this.state = {
      email: '',
      password: '',
      touched: {
        email: false,
        password: false,
      },
    };
  }

  componentDidMount() {
    document.title = 'SimplexServices | Login';
  }

  handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  handleBlur = (field: 'email' | 'password') => {
    this.setState((prevState) => ({
      touched: { ...prevState.touched, [field]: true },
    }));
  };

  handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { email, password } = this.state;
    if (this.isFormValid()) {
      this.props.loginUser(email, password);
    }
  };

  isFormValid = (): boolean => {
    const { email, password } = this.state;
    return email.trim() !== '' && password !== '';
  };

  isFieldInvalid = (field: 'email' | 'password'): boolean => {
    const { touched } = this.state;
    const value = this.state[field];
    return !value && touched[field];
  };

  render() {
    const { isLoggedIn, message } = this.props;
    const { email, password } = this.state;

    // Redirect if already logged in
    if (isLoggedIn) {
      return <Redirect to="/dashboard" />;
    }

    return (
      <div className="page login-page">
        <div className="container d-flex align-items-center">
          <div className="form-holder has-shadow">
            <div className="row">
              {/* Logo & Information Panel */}
              <div className="col-lg-6">
                <div className="info d-flex align-items-center justify-content-center">
                  <div className="content text-center">
                    <div className="logo">
                      <img
                        src={`${process.env.PUBLIC_URL}/assets/img/logo.png`}
                        width="235"
                        height="235"
                        alt="SimpleX Services Logo"
                        className="img-fluid"
                      />
                      <h1 className="mt-3">SimpleX Services</h1>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Panel */}
              <div className="col-lg-6 bg-white">
                <div className="form d-flex align-items-center">
                  <div className="content">
                    {/* Error Message */}
                    {message && (
                      <p
                        className="text-center text-danger mb-4"
                        style={{ fontWeight: 'bold' }}
                      >
                        {message}
                      </p>
                    )}

                    <form className="form-validate" onSubmit={this.handleSubmit}>
                      {/* Email Field */}
                      <div className="form-group">
                        <label htmlFor="email" className="form-control-label">
                          Email
                        </label>
                        <input
                          id="email"
                          type="email"
                          name="email"
                          value={email}
                          onChange={this.handleInputChange}
                          onBlur={() => this.handleBlur('email')}
                          required
                          className={`input-material ${this.isFieldInvalid('email') ? 'is-invalid' : ''}`}
                        />
                        {this.isFieldInvalid('email') && (
                          <div className="invalid-feedback">Email is required.</div>
                        )}
                      </div>

                      {/* Password Field */}
                      <div className="form-group-material">
                        <label htmlFor="password" className="form-control-label">
                          Password
                        </label>
                        <input
                          id="login-password"
                          type="password"
                          name="password"
                          value={password}
                          onChange={this.handleInputChange}
                          onBlur={() => this.handleBlur('password')}
                          required
                          className={`input-material ${this.isFieldInvalid('password') ? 'is-invalid' : ''}`}
                        />
                        {this.isFieldInvalid('password') && (
                          <div className="invalid-feedback">Password is required.</div>
                        )}
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={!this.isFormValid()}
                        className="btn btn-block btn-primary mt-3"
                      >
                        Login
                      </button>
                    </form>

                    <Link to="/forgot" className="text-info d-block mt-3 text-center">
                      Forgot Password?
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="copyrights text-center mt-4">
          <p>
            Powered By{' '}
            <a
              href="https://www.simplexts.net"
              target="_blank"
              rel="noopener noreferrer"
              className="external text-primary"
            >
              SimpleX Technology Solutions
            </a>
          </p>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  message: state.user.message || '',
  isLoggedIn: state.user.isLoggedIn || false,
});

const mapDispatchToProps = (dispatch: any) => ({
  loginUser: (username: string, password: string) =>
    dispatch(loginUser(username, password)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);